import React, { useState } from 'react';
import { Carousel } from '../../modules/Carousel';
import { clientSideLogger as logger } from '../../shared/libs/ErrorLogger';
import VideoCover from 'react-video-cover';
import {
  RightArrowIconCollection,
  LeftArrowIconCollection,
  AssetIcon
} from '../../modules/Icons';
import { CarouselArrowProps } from '../../types/pages/homepage/HomepageTypes';
import { CollectionSliderResponse } from '../../types/pages/homepage/HomepageTypes';
import { getCollectionPath } from '../../helpers/pages/collections/collectionsHelpers';
import { QUERY_PARAMS as ALGOLIA_QUERY_PARAMS } from '../../constants/algolia';
import { useRouter } from 'next/router';
import Link from 'next/link';

const RelatedCollectionSliderContainer: React.FC<CollectionSliderResponse> = (props) => {
  const [collectionsLength, setCollectionsLength] = useState<number>(props.collections.length);
  const [hovered, setHovered] = useState(false);
  const router = useRouter();
  const carouselOptions = {
    arrows: collectionsLength > 3 ? props.arrows !== false : props.arrows === false,
    nextArrow: props.arrows === false ? false : <SliderCarouselArrow next />,
    prevArrow: props.arrows === false ? false : <SliderCarouselArrow />,
    rows: 1,
    slidesToShow: props.collections.length === 1 ? 1 : 2,
    slidesToScroll: 1,
    dots: collectionsLength <= 3 ? false : true,
    variableWidth: true,
    infinite: false,
    responsive: [
      {
        breakpoint: 1536,
        settings: {
          slidesToShow: collectionsLength === 1 ? 1 : 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: collectionsLength === 1 ? 1 : 2,
          slidesToScroll: 1,
          dots: collectionsLength <= 3 ? false : true
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: collectionsLength === 1 ? 1 : 1.5,
          slidesToScroll: 1,
          dots: false
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: false
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: false,
          swipeToSlide: collectionsLength === 1 ? false : true
        }
      },
      {
        breakpoint: 400,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: false,
          swipeToSlide: collectionsLength === 1 ? false : true
        }
      }
    ]
  };

  const collectionSlides: JSX.Element[] = props.collections.map((item, index) => {
    let videoRef: any = React.createRef<HTMLVideoElement>();
    let previewPlaying: boolean = false;
    let validPreview: boolean = Boolean(item.media.mp4);
    const videoOptions = {
      src: item.media.mp4,
      muted: true,
      playsInline: true,
      preload: 'none',
      loop: true,
      ref: (videoElem) => {
        videoRef = videoElem;
      },
      'data-testid': 'collectionVideoPreview'
    };

    async function onHover(): Promise<void> {
      setHovered(true);
      if (!validPreview) return null;
      if (!videoRef) return null;
      try {
        videoRef.classList.remove('hidden');
        await videoRef.play();
        previewPlaying = true;
      } catch (err) {
        logger.warning(err);
        previewPlaying = false;
      }
    }

    function onLeave(): void {
      setHovered(false);
      if (!videoRef) return null;
      if (!validPreview) return null;
      videoRef.classList.add('hidden');
      videoRef.currentTime = 0;
      if (previewPlaying) videoRef.pause();
      previewPlaying = false;
    }

    return (
      <div
        key={`collection-slide-${index}`}
        className="text-gray-400 !fill-[#AFAFB0] hover:text-white hover:!fill-white"
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
      >
        <Link href={getCollectionPath(item.slug, item.library, item.sku, index, router.query[ALGOLIA_QUERY_PARAMS.QUERY_ID] as string)} className="flex flex-col">
          <div
            className="custom-rounded-box hover:shadow-md"
            style={{ backgroundImage: `url(${item.media.image})` }}
          >
            <div className="hover:border hover:border-[#4D5457] top-0 h-[100%] w-[100%] rounded-[5px] overflow-hidden">
              <VideoCover videoOptions={videoOptions} />
            </div>
          </div>
          <div className="flex items-start max-w-[96%] py-2">
            <div className="flex flex-grow">
              <h2 className="text-[14px] lg:text-[14px] px-1 leading-snug inter">{item.name}</h2>
            </div>

            <div className="flex gap-1 text-right items-start text-[14px] font-light mr-2">
              <AssetIcon />
              {item.elements}
            </div>
          </div>
        </Link>
      </div>
    );
  });

  return (
    <div className="relatedCollectionCarousel relative pb-0">
      <Carousel carouselElements={collectionSlides} carouselOptions={carouselOptions} />
    </div>
  );
};

const SliderCarouselArrow: React.FC<CarouselArrowProps> = (props) => {
  const opacity =
    (props.currentSlide === 0 && props.next) || (!props.next && props.currentSlide !== 0)
      ? 'opacity-100'
      : 'opacity-60';

  const ArrowIconCollection = props.next ? RightArrowIconCollection : LeftArrowIconCollection;
  const arrowClass = props.next
    ? `rightArrowCollection ${opacity} hidden top-[35%] xl:block xl:scale-[70%] xl:mr-[-2px] 1920p:mr-[-5px] 4k:scale-[90%] 4k:mr-[-10px] 4k:top-[40%] ultra-wide:mr-[-15px]`
    : `leftArrowCollection ${opacity} hidden top-[35%] xl:block xl:scale-[70%]  2xl:ml-[-5px] 4k:scale-[90%] 4k:ml-[-2px] 4k:top-[40%] ultra-wide:ml-[-5px]`;

  return (
    <div className={arrowClass} onClick={props.onClick}>
      <ArrowIconCollection />
    </div>
  );
};

export default RelatedCollectionSliderContainer;
